import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EventPnlNameLink } from '@/components/event-pnl';
import { eventPnlPath } from '@/lib/event-pnl';
import {
  DataTable,
  DatePicker,
  EmptyState,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  cn,
  type DataTableCard,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  CONTRIBUTION_NOTE,
  EARLY_YEARS_NOTE,
  MARGIN_TONE,
  NO_VALUE,
  PROGRAMS_DEFAULT_FROM,
  PROGRAMS_PARAM,
  PROGRAM_MODE,
  PROGRAM_MODE_OPTIONS,
  formatEventDay,
  formatMoney,
  formatPercent,
  marginTone,
  opportunityUrl,
  parseProgramMode,
  statementUrl,
  type MarginTone,
  type ProgramEventRow,
  type ProgramMode,
  type ProgramsQuery,
} from '@/lib/program-profitability';

/** Pieces the Programs list, losers view and program detail share. */

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The window and mode, from the URL with the page defaults (2023 to today,
 * all events), plus setters that keep defaults out of the URL.
 */
export function useProgramsQuery(): {
  readonly query: ProgramsQuery;
  readonly setDate: (key: 'from' | 'to', value: string) => void;
  readonly setMode: (mode: ProgramMode) => void;
} {
  const [params, setParams] = useSearchParams();
  const query: ProgramsQuery = {
    from: params.get(PROGRAMS_PARAM.from) || PROGRAMS_DEFAULT_FROM,
    to: params.get(PROGRAMS_PARAM.to) || today(),
    mode: parseProgramMode(params.get(PROGRAMS_PARAM.mode)),
  };

  function update(key: string, value: string | null): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  return {
    query,
    setDate: (key, value) => update(PROGRAMS_PARAM[key], value),
    setMode: (mode) => update(PROGRAMS_PARAM.mode, mode === PROGRAM_MODE.all ? null : mode),
  };
}

/** From / To pickers and the In person / Virtual / All toggle. */
export function ProgramsFilters({
  query,
  setDate,
  setMode,
}: ReturnType<typeof useProgramsQuery>): React.ReactElement {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="programs-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
          From
        </label>
        <DatePicker id="programs-from" value={query.from} placeholder="Start" onChange={(value) => setDate('from', value)} />
      </div>
      <div>
        <label htmlFor="programs-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
          To
        </label>
        <DatePicker id="programs-to" value={query.to} placeholder="End" max={today()} onChange={(value) => setDate('to', value)} />
      </div>
      <div>
        <span className="mb-1 block text-portal-micro font-bold text-portal-muted">Events</span>
        <SegmentedControl<ProgramMode> label="Which events to count" value={query.mode} onChange={setMode} options={PROGRAM_MODE_OPTIONS} />
      </div>
    </div>
  );
}

/** What contribution leaves out, and the 2023 to 2024 caveat. */
export function ContributionNotes(): React.ReactElement {
  return (
    <div className="mb-4 space-y-1 text-portal-micro text-portal-muted">
      <p>{CONTRIBUTION_NOTE}</p>
      <p>{EARLY_YEARS_NOTE}</p>
    </div>
  );
}

const MARGIN_CLASS: Readonly<Record<MarginTone, string>> = {
  [MARGIN_TONE.loss]: 'text-portal-danger font-bold',
  [MARGIN_TONE.below]: 'text-amber-700',
  [MARGIN_TONE.healthy]: 'text-portal-ink',
  [MARGIN_TONE.none]: 'text-portal-muted',
};

/** A right-aligned margin cell, red for a loss and amber below target. */
export function MarginCell({ margin, target }: { readonly margin: number | null; readonly target: number }): React.ReactElement {
  return (
    <td className={cn('whitespace-nowrap p-3 text-right text-portal-small tabular-nums', MARGIN_CLASS[marginTone(margin, target)])}>
      {formatPercent(margin)}
    </td>
  );
}

/** A right-aligned dollar cell. */
export function MoneyCell({ value, strong = false }: { readonly value: number; readonly strong?: boolean }): React.ReactElement {
  return (
    <td
      className={cn(
        'whitespace-nowrap p-3 text-right text-portal-small tabular-nums',
        strong && 'font-bold',
        value < 0 ? 'text-portal-danger' : 'text-portal-ink',
      )}
    >
      {formatMoney(value)}
    </td>
  );
}

/** A right-aligned muted figure (secondary columns). */
export function FigureCell({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{children}</td>;
}

const EVENT_SORT_FIELDS = {
  event: 'opportunityName',
  date: 'eventDate',
  program: 'program',
  participants: 'participants',
  revenue: 'revenue',
  cost: 'itemizedCost',
  contribution: 'contribution',
  margin: 'margin',
} as const;
const EVENT_SORT_KEYS = Object.values(EVENT_SORT_FIELDS);

export const EVENT_DEFAULT_SORT = {
  byMargin: { key: EVENT_SORT_FIELDS.margin, direction: 'asc' },
  byContribution: { key: EVENT_SORT_FIELDS.contribution, direction: 'asc' },
} as const satisfies Record<string, DataTableSort>;

const EVENT_SEARCH_FIELDS = ['opportunityName', 'accountName', 'program', 'statementName'] as const;

function SalesforceLinks({ event }: { readonly event: ProgramEventRow }): React.ReactElement {
  return (
    <span className="whitespace-nowrap">
      <a href={opportunityUrl(event.opportunityId)} target="_blank" rel="noreferrer" className="text-brand hover:underline">
        Opportunity
      </a>
      <span className="text-portal-muted"> · </span>
      <a href={statementUrl(event.statementId)} target="_blank" rel="noreferrer" className="text-brand hover:underline">
        Statement
      </a>
    </span>
  );
}

/**
 * The searchable, sortable event table the program detail and the losers
 * view share. Sort and search live in the URL; the rows are fully loaded.
 */
export function ProgramEventsTable({
  events,
  target,
  showProgram,
  defaultSort,
  searchLabel,
}: {
  readonly events: readonly ProgramEventRow[];
  readonly target: number;
  /** Show the Program column (the losers view spans programs). */
  readonly showProgram: boolean;
  readonly defaultSort: DataTableSort;
  readonly searchLabel: string;
}): React.ReactElement {
  const { sort, onSort } = useTableSort(EVENT_SORT_KEYS, defaultSort);
  const { search, setSearch } = useUrlSearch();
  const navigate = useNavigate();
  const rows = useMemo(() => sortRows(filterRows(events, search, [...EVENT_SEARCH_FIELDS]), sort), [events, search, sort]);

  const headers = [
    { label: 'Event', sortKey: EVENT_SORT_FIELDS.event },
    { label: 'Date', sortKey: EVENT_SORT_FIELDS.date },
    ...(showProgram ? [{ label: 'Program', sortKey: EVENT_SORT_FIELDS.program }] : []),
    { label: 'Participants', sortKey: EVENT_SORT_FIELDS.participants },
    { label: 'Revenue', sortKey: EVENT_SORT_FIELDS.revenue },
    { label: 'Itemized cost', sortKey: EVENT_SORT_FIELDS.cost },
    { label: 'Contribution', sortKey: EVENT_SORT_FIELDS.contribution },
    { label: 'Margin', sortKey: EVENT_SORT_FIELDS.margin },
    'Salesforce',
  ];
  const firstNumeric = showProgram ? 3 : 2;
  const rightAligned = [0, 1, 2, 3, 4].map((offset) => firstNumeric + offset);

  const cards: DataTableCard[] = rows.map((event) => ({
    key: event.statementId,
    title: event.opportunityName || event.statementName,
    subtitle: [formatEventDay(event.eventDate), showProgram ? event.program : null].filter(Boolean).join(' · '),
    fields: [
      { label: 'Revenue', value: formatMoney(event.revenue) },
      { label: 'Itemized cost', value: formatMoney(event.itemizedCost) },
      { label: 'Contribution', value: formatMoney(event.contribution) },
      { label: 'Margin', value: formatPercent(event.margin) },
    ],
    onOpen: () => navigate(eventPnlPath(event.opportunityId)),
    action: <SalesforceLinks event={event} />,
  }));

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label={searchLabel}
          placeholder="Client, opportunity or statement"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <span className="text-portal-micro text-portal-muted">
          {rows.length} {rows.length === 1 ? 'event' : 'events'}
        </span>
      </div>
      {rows.length === 0 ? (
        <Panel>
          {search.trim() !== '' ? (
            <EmptyState title="No events match" message="Try a different search." />
          ) : (
            <EmptyState title="No events in this window" message="Widen the dates or switch to all events." />
          )}
        </Panel>
      ) : (
        <DataTable headers={headers} rightAlignedHeaders={rightAligned} sort={sort} onSort={onSort} minWidth={showProgram ? 1180 : 1000} cards={cards}>
          {rows.map((event) => (
            <tr key={event.statementId} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <EventPnlNameLink opportunityId={event.opportunityId} name={event.opportunityName || event.statementName} bold showSalesforce={false} />
                <span className="block text-portal-micro text-portal-muted">
                  {event.accountName || NO_VALUE}
                  {event.itemized ? null : (
                    <span className="ml-2">
                      <Pill tone="warning">No expense lines</Pill>
                    </span>
                  )}
                </span>
              </td>
              <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{formatEventDay(event.eventDate)}</td>
              {showProgram ? <td className="p-3 text-portal-small text-portal-muted">{event.program}</td> : null}
              <FigureCell>{event.participants ?? NO_VALUE}</FigureCell>
              <MoneyCell value={event.revenue} />
              <MoneyCell value={event.itemizedCost} />
              <MoneyCell value={event.contribution} strong />
              <MarginCell margin={event.margin} target={target} />
              <td className="p-3 text-portal-small">
                <SalesforceLinks event={event} />
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </>
  );
}
